import React, { useState, useEffect } from 'react'
import { ToastContainer, Toast, Alert, Button } from 'react-bootstrap'
import { IoMdCloseCircleOutline } from 'react-icons/io'
import s from './ToastAlert.module.scss'

interface ToastAlertItemProps {
  show?: boolean
  variant?: 'success' | 'danger'
  onChange?: (newState: boolean) => void
  delay?: number
  children: React.ReactNode
}
const toastAlertItemDefaultProps = {
  show: true,
  onChange: undefined,
  variant: 'danger',
  delay: 5000,
}

const ToastAlertItem = ({
  children,
  show = toastAlertItemDefaultProps.show,
  onChange,
  variant,
  delay = toastAlertItemDefaultProps.delay,
}: ToastAlertItemProps) => {
  const [state, setState] = useState<boolean>(show)

  const handleClose = () => setState(false)

  useEffect(() => {
    if (show !== state) setState(show)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't want to update state when it changes else infinite loop
  }, [show])

  useEffect(() => {
    if (onChange) onChange(state)
  }, [state, onChange])

  return (
    <Toast
      className={s.item}
      autohide
      show={state}
      delay={delay}
      onClose={handleClose}
    >
      <Toast.Body className={s.body} as={Alert} variant={variant}>
        <Button className={s.close} variant="none" onClick={handleClose}>
          <IoMdCloseCircleOutline />
        </Button>
        {children}
      </Toast.Body>
    </Toast>
  )
}

ToastAlertItem.defaultProps = toastAlertItemDefaultProps

interface ToastAlertContainerProps<T = typeof ToastAlertItem> {
  children: React.ReactElement<T> | React.ReactElement<T>[]
}

const ToastAlertContainer = ({ children }: ToastAlertContainerProps) => (
  <ToastContainer className={s.container} position="top-center">
    {children}
  </ToastContainer>
)

type ToastAlertProps = ToastAlertItemProps

const toastAlertDefaultProps = {
  show: toastAlertItemDefaultProps.show,
  onChange: toastAlertItemDefaultProps.onChange,
  variant: toastAlertItemDefaultProps.variant,
  delay: toastAlertItemDefaultProps.delay,
}

/**
 *  Will display alerts that will fade after a delay
 *
 *  HOW TO USE:
 *  - Parent must be in position relative
 *
 *  To show single alert use ToastAlert:
 *  ```jsx
 *  <ToastAlert variant="danger" show={trueCondition}>
 *    "Your message"
 *  </ToastAlert>
 *  ```
 *
 *  To show many alerts use ToastAlert.Container with ToastAlert.Item:
 *  ```jsx
 *  <ToastAlert.Container>
 *    <ToastAlert.Item variant="success" show={trueCondition}>
 *      <p>Your success message</p>
 *    </ToastAlert.Item>
 *    <ToastAlert.Item variant="danger" show={trueCondition}>
 *      <p>Your error message</p>
 *    </ToastAlert.Item>
 *  </ToastAlert.Container>
 *  ```
 */
const ToastAlert = ({
  children,
  show,
  variant,
  delay,
}: ToastAlertProps): JSX.Element => (
  <ToastAlertContainer>
    <ToastAlertItem show={show} variant={variant} delay={delay}>
      {children}
    </ToastAlertItem>
  </ToastAlertContainer>
)
ToastAlert.defaultProps = toastAlertDefaultProps

ToastAlert.Container = ToastAlertContainer
ToastAlert.Item = ToastAlertItem
export default ToastAlert
